// src/utils.ts
function ensure(value) {
  if (value == null) {
    throw new Error("Unable to return a nullish value.");
  }
  return value;
}
function generateKey() {
  return Math.random().toString(36).substr(2, 9);
}
function defaultShouldUpdate(a, b) {
  return !Object.is(a, b);
}
function defaultSerialize(...params) {
  return JSON.stringify(params);
}

// src/graph-node.ts
var NODES = new Map();
var KEYS = new Map();
function createKey(key) {
  if (key) {
    if (process.env.NODE_ENV === "production") {
      if (KEYS.has(key)) {
        return ensure(KEYS.get(key));
      }
      const newKey = generateKey();
      KEYS.set(key, newKey);
      return newKey;
    }
    return key;
  }
  return generateKey();
}
function createRawGraphNode(options) {
  var _a;
  const key = createKey(options.key);
  const shouldUpdate = (_a = options.shouldUpdate) != null ? _a : defaultShouldUpdate;
  if ("set" in options) {
    return {
      get: options.get,
      set: options.set,
      key,
      shouldUpdate
    };
  }
  const newNode = {
    get: options.get,
    set: (context, action) => {
      context.mutateSelf(action);
    },
    key,
    shouldUpdate
  };
  return newNode;
}
function node(options) {
  if (options.key != null) {
    const currentNode = NODES.get(createKey(options.key));
    if (currentNode) {
      return currentNode;
    }
  }
  const newNode = "set" in options ? createRawGraphNode(options) : createRawGraphNode(options);
  NODES.set(newNode.key, newNode);
  return newNode;
}
function createGraphNode(options) {
  return "set" in options ? node(options) : node(options);
}
function factory(options) {
  var _a;
  const factoryKey = `Factory[${(_a = options.factoryKey) != null ? _a : generateKey()}]`;
  if ("set" in options) {
    return (...args) => node({
      key: `${factoryKey}(${options.key ? options.key(...args) : defaultSerialize(args)}`,
      get: options.get(...args),
      set: options.set(...args),
      shouldUpdate: options.shouldUpdate ? options.shouldUpdate(...args) : void 0
    });
  }
  return (...args) => node({
    key: `${factoryKey}(${options.key ? options.key(...args) : defaultSerialize(args)}`,
    get: options.get(...args),
    shouldUpdate: options.shouldUpdate ? options.shouldUpdate(...args) : void 0
  });
}
function createGraphNodeFactory(options) {
  return "set" in options ? factory(options) : factory(options);
}

// src/devtools.ts
import { addTransformer, stringify, withRecursionTracker } from "ecmason";
addTransformer("object", {
  tag: "PROMISE",
  check: (value) => value instanceof Promise,
  serialize: () => null,
  deserialize: () => Promise.resolve()
});
addTransformer("primitive", {
  tag: "FUNCTION",
  check: (v) => typeof v === "function",
  serialize: (v) => `ƒ ${v.name} () { }`,
  deserialize: (v) => {
    const newFunc = () => {
    };
    newFunc.name = v;
    return newFunc;
  }
});
addTransformer("object", withRecursionTracker({
  tag: "ERROR",
  check: (v) => v instanceof Error,
  serialize: (v) => ({
    name: v.name,
    message: v.message
  }),
  deserialize: (v) => {
    const error = new Error(v.message);
    error.name = v.name;
    return error;
  }
}));
function updateData(type, data) {
  if (process.env.NODE_ENV !== "production" && typeof document !== "undefined") {
    document.dispatchEvent(new CustomEvent("__GRAPH_STATE__", {
      detail: {
        type,
        data
      }
    }));
  }
}
function exposeMemory(memory) {
  if (process.env.NODE_ENV !== "production" && typeof document !== "undefined") {
    updateData("MEMORY", memory);
  }
}
function exposeNode(memory, key, data) {
  if (process.env.NODE_ENV !== "production" && typeof document !== "undefined") {
    updateData("NODE", {
      memory,
      key,
      data: stringify(data)
    });
  }
}

// src/core.ts
function defaultBatcher(callback) {
  callback();
}
function parseDependencies(dependencies) {
  return Array.from(dependencies).map((node2) => node2.key);
}
function createMemory(batcher = defaultBatcher, key) {
  const memory = {
    key: key != null ? key : generateKey(),
    nodes: new Map(),
    batcher
  };
  exposeMemory(memory.key);
  return memory;
}
function createGraphDomainMemory(batcher = defaultBatcher) {
  return createMemory(batcher);
}
function createVersion() {
  return {
    alive: true,
    cleanups: []
  };
}
function getInstance(memory, node2, hydrateValue) {
  if (memory.nodes.has(node2.key)) {
    return ensure(memory.nodes.get(node2.key));
  }
  const dependencies = new Set();
  const getterVersion = createVersion();
  const baseNode = {
    getterVersion,
    setterVersion: createVersion(),
    listeners: new Set(),
    dependents: new Set(),
    dependencies,
    state: {
      version: 0,
      value: hydrateValue ? hydrateValue.value : createState(memory, node2, getterVersion, dependencies)
    }
  };
  memory.nodes.set(node2.key, baseNode);
  exposeNode(memory.key, node2.key, {
    state: baseNode.state.value,
    dependents: parseDependencies(baseNode.dependents),
    dependencies: parseDependencies(baseNode.dependencies),
    listeners: baseNode.listeners.size
  });
  return baseNode;
}
function isNodeGetFunction(value) {
  return typeof value === "function";
}
function createState(memory, node2, getterVersion, dependencies) {
  if (isNodeGetFunction(node2.get)) {
    return node2.get({
      get: (dependency) => {
        const instance = getInstance(memory, dependency);
        if (getterVersion.alive) {
          dependencies.add(dependency);
          instance.dependents.add(node2);
        }
        return instance.state.value;
      },
      getSelf: () => get(memory, node2),
      mutate: (target, value) => {
        if (getterVersion.alive) {
          set(memory, target, value);
        }
      },
      mutateSelf: (value) => {
        if (getterVersion.alive) {
          set(memory, node2, value);
        }
      },
      set: (target, action) => dispatch(memory, target, action),
      setSelf: (action) => dispatch(memory, node2, action),
      reset: (target) => {
        if (getterVersion.alive) {
          reset(memory, target);
        }
      },
      resetSelf: () => {
        if (getterVersion.alive) {
          reset(memory, node2);
        }
      },
      subscription: (callback) => {
        if (getterVersion.alive) {
          const cleanup = callback();
          if (cleanup) {
            getterVersion.cleanups.push(cleanup);
          }
        }
      },
      resolve: (promise) => new Promise((resolve, reject) => {
        promise.then((result) => {
          if (getterVersion.alive) {
            resolve(result);
          }
        }, (result) => {
          if (getterVersion.alive) {
            reject(result);
          }
        });
      })
    });
  }
  return node2.get;
}
function getRef(memory, node2) {
  const actualNode = getInstance(memory, node2);
  return actualNode.state;
}
function get(memory, node2) {
  return getRef(memory, node2).value;
}
function getGraphNodeState(memory, node2) {
  return get(memory, node2);
}
function dispatch(memory, node2, action) {
  const actualNode = getInstance(memory, node2);
  actualNode.setterVersion.cleanups.forEach((cleanup) => {
    cleanup();
  });
  const setterVersion = createVersion();
  actualNode.setterVersion.alive = false;
  actualNode.setterVersion = setterVersion;
  return node2.set({
    get: (target) => get(memory, target),
    getSelf: () => get(memory, node2),
    set: (target, targetAction) => dispatch(memory, target, targetAction),
    setSelf: (targetAction) => dispatch(memory, node2, targetAction),
    mutate: (target, targetValue) => {
      if (setterVersion.alive) {
        set(memory, target, targetValue);
      }
    },
    mutateSelf: (targetValue) => {
      if (setterVersion.alive) {
        set(memory, node2, targetValue);
      }
    },
    reset: (target) => {
      if (setterVersion.alive) {
        reset(memory, target);
      }
    },
    resetSelf: () => {
      if (setterVersion.alive) {
        reset(memory, node2);
      }
    },
    resolve: (promise) => new Promise((resolve, reject) => {
      promise.then((result) => {
        if (setterVersion.alive) {
          resolve(result);
        }
      }, (result) => {
        if (setterVersion.alive) {
          reject(result);
        }
      });
    }),
    subscription: (callback) => {
      if (setterVersion.alive) {
        const cleanup = callback();
        if (cleanup) {
          setterVersion.cleanups.push(cleanup);
        }
      }
    }
  }, action);
}
function runGraphNodeDispatch(memory, node2, action) {
  return dispatch(memory, node2, action);
}
function reset(memory, node2) {
  const actualNode = getInstance(memory, node2);
  actualNode.dependencies.forEach((dependency) => {
    getInstance(memory, dependency).dependents.delete(node2);
  });
  actualNode.getterVersion.cleanups.forEach((cleanup) => {
    cleanup();
  });
  actualNode.dependencies.clear();
  actualNode.getterVersion.alive = false;
  actualNode.getterVersion = createVersion();
  const newState = createState(memory, node2, actualNode.getterVersion, actualNode.dependencies);
  set(memory, node2, newState);
}
function runGraphNodeCompute(memory, node2) {
  reset(memory, node2);
}
function hydrate(memory, node2, value) {
  getInstance(memory, node2, {
    value
  });
}
function hydrateGraphNodeState(memory, node2, value) {
  hydrate(memory, node2, value);
}
function isActionFunction(value) {
  return typeof value === "function";
}
function set(memory, node2, value, notify = true) {
  const actualNode = getInstance(memory, node2);
  const newState = isActionFunction(value) ? value(actualNode.state.value) : value;
  if (node2.shouldUpdate(actualNode.state.value, newState)) {
    memory.batcher(() => {
      actualNode.state.value = newState;
      actualNode.state.version += 1;
      new Set(actualNode.dependents).forEach((dependent) => {
        reset(memory, dependent);
      });
      if (notify) {
        actualNode.listeners.forEach((listener) => {
          listener(newState);
        });
      }
      exposeNode(memory.key, node2.key, {
        state: newState,
        dependents: parseDependencies(actualNode.dependents),
        dependencies: parseDependencies(actualNode.dependencies),
        listeners: actualNode.listeners.size
      });
    });
  }
}
function setGraphNodeState(memory, node2, value, notify = true) {
  set(memory, node2, value, notify);
}
function subscribe(memory, node2, listener) {
  const actualNode = getInstance(memory, node2);
  actualNode.listeners.add(listener);
  exposeNode(memory.key, node2.key, {
    state: actualNode.state.value,
    dependents: parseDependencies(actualNode.dependents),
    dependencies: parseDependencies(actualNode.dependencies),
    listeners: actualNode.listeners.size
  });
  return () => {
    exposeNode(memory.key, node2.key, {
      state: actualNode.state.value,
      dependents: parseDependencies(actualNode.dependents),
      dependencies: parseDependencies(actualNode.dependencies),
      listeners: actualNode.listeners.size
    });
    actualNode.listeners.delete(listener);
  };
}
function subscribeGraphNode(memory, node2, listener) {
  return subscribe(memory, node2, listener);
}
function destroyMemory(memory) {
  memory.nodes.forEach((node2) => {
    node2.getterVersion.cleanups.forEach((cleanup) => {
      cleanup();
    });
  });
  memory.nodes.clear();
}
function destroyGraphDomainMemory(memory) {
  destroyMemory(memory);
}
function exists(memory, node2) {
  return memory.nodes.has(node2.key);
}
function hasGraphNode(memory, node2) {
  return exists(memory, node2);
}
function version(memory, node2) {
  return getRef(memory, node2).version;
}
function getGraphNodeVersion(memory, node2) {
  return version(memory, node2);
}

// src/resource.ts
function promiseToResource(promise, mutate) {
  promise.then((data) => mutate({
    status: "success",
    data
  }), (data) => mutate({
    status: "failure",
    data
  }));
  return {
    data: promise,
    status: "pending"
  };
}
function resource(reference) {
  return node({
    get: (context) => promiseToResource(context.get(reference), (value) => context.mutateSelf(value)),
    key: `Resource(${reference.key})`
  });
}
function createGraphNodeResource(reference) {
  return resource(reference);
}
function fromResource(reference) {
  return node({
    get: async (context) => {
      const result = context.get(reference);
      if (result.status === "failure") {
        throw result.data;
      }
      return result.data;
    },
    key: `Promise(${reference.key})`
  });
}
function joinResourceKeys(resources) {
  return resources.map((reference) => reference.key).join(", ");
}
function waitForAll(resources) {
  const promises = resources.map((reference) => fromResource(reference));
  return node({
    get: ({ get: get2, mutateSelf }) => promiseToResource(Promise.all(promises.map((promise) => get2(promise))), mutateSelf),
    key: `WaitForAll(${joinResourceKeys(resources)})`
  });
}
function waitForAny(resources) {
  const promises = resources.map((reference) => fromResource(reference));
  return node({
    get: ({ get: get2, mutateSelf }) => promiseToResource(Promise.race(promises.map((promise) => get2(promise))), mutateSelf),
    key: `WaitForAny(${joinResourceKeys(resources)})`
  });
}
function joinResources(resources) {
  return node({
    get: ({ get: get2 }) => resources.map((reference) => get2(reference)),
    key: `JoinedResource(${joinResourceKeys(resources)})`
  });
}
function resourceFactory(factory2) {
  return (...params) => resource(factory2(...params));
}
function createGraphNodeResourceFactory(factory2) {
  return resourceFactory(factory2);
}
export {
  createGraphDomainMemory,
  createGraphNode,
  createGraphNodeFactory,
  createGraphNodeResource,
  createGraphNodeResourceFactory,
  createMemory,
  destroyGraphDomainMemory,
  destroyMemory,
  dispatch,
  exists,
  factory,
  fromResource,
  get,
  getGraphNodeState,
  getGraphNodeVersion,
  hasGraphNode,
  hydrate,
  hydrateGraphNodeState,
  joinResources,
  node,
  reset,
  resource,
  resourceFactory,
  runGraphNodeCompute,
  runGraphNodeDispatch,
  set,
  setGraphNodeState,
  subscribe,
  subscribeGraphNode,
  version,
  waitForAll,
  waitForAny
};
/**
 * @license
 * MIT License
 *
 * Copyright (c) 2020 Alexis Munsayac
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 *
 * @author Alexis Munsayac <alexis.munsayac@gmail.com>
 * @copyright Alexis Munsayac 2020
 */
/**
 * @license
 * MIT License
 *
 * Copyright (c) 2021 Alexis Munsayac
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 *
 * @author Alexis Munsayac <alexis.munsayac@gmail.com>
 * @copyright Alexis Munsayac 2021
 */
//# sourceMappingURL=index.js.map
